import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Download, TableChart } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { downloadCsvFile } from '~/utils/csvDownload';

import DateRangeSlider from './DateRangeSlider';
import SupplierFilterChips from './SupplierFilterChips';
import { recomputeAnalysis } from './analyzeParityCsv';
import { BEST_COMPETITOR_LABEL, LE_SUPPLIER_LABEL, LE_VS_BEST_ROW_LABEL, PARITY_SECTION_BLOCK_MB } from './constants';
import { downloadDomElementAsPng } from './downloadDomElementAsPng';
import { type LeVsBestDisplayMode, ParityAnalysis, TRACKED_OTAS } from './types';
import { useParityReportDateRange } from './useParityReportDateRange';
import {
  buildMonthGroups,
  buildSupplierPriceMap,
  deriveLowestPriceByDate,
  deriveVisibleOtas,
  fmtShortDate,
  formatMatrixCellDisplay,
  getHeatmapColors,
  matrixCellExportValue,
} from './utils';

interface Props {
  analysis: ParityAnalysis;
}

function CalendarRateView({ analysis }: Props) {
  const [hiddenSuppliers, setHiddenSuppliers] = useState<Set<string>>(new Set());
  const [leVsBestDisplayMode, setLeVsBestDisplayMode] = useState<LeVsBestDisplayMode>('percentDiff');

  const activeCalendarOtas = useMemo(() => deriveVisibleOtas(hiddenSuppliers), [hiddenSuppliers]);

  const calendarAnalysis = useMemo(
    () => recomputeAnalysis(analysis, activeCalendarOtas),
    [analysis, activeCalendarOtas],
  );

  const dates = useMemo(() => calendarAnalysis.dailyDetail.map((d) => d.date), [calendarAnalysis.dailyDetail]);
  const totalDays = dates.length;
  const { dateRange, setDateRange, committedDateRange, setCommittedDateRange } = useParityReportDateRange(
    totalDays,
    analysis,
  );

  const handleRangeCommit = useCallback(
    (r: Array<number>) => {
      setDateRange(r);
      setCommittedDateRange(r);
    },
    [setDateRange, setCommittedDateRange],
  );

  const visibleDailyDetail = useMemo(
    () => calendarAnalysis.dailyDetail.slice(committedDateRange[0], committedDateRange[1] + 1),
    [calendarAnalysis.dailyDetail, committedDateRange],
  );
  const visibleDates = useMemo(
    () => dates.slice(committedDateRange[0], committedDateRange[1] + 1),
    [dates, committedDateRange],
  );

  const supplierPrices = useMemo(() => buildSupplierPriceMap(visibleDailyDetail), [visibleDailyDetail]);

  const monthGroups = useMemo(() => buildMonthGroups(visibleDailyDetail), [visibleDailyDetail]);

  const visibleRows = useMemo(() => {
    const otas: Array<string> = [...TRACKED_OTAS].filter((ota) => !hiddenSuppliers.has(ota));
    const rows = [...otas, LE_SUPPLIER_LABEL, LE_VS_BEST_ROW_LABEL];
    if (!hiddenSuppliers.has(BEST_COMPETITOR_LABEL)) {
      rows.push(BEST_COMPETITOR_LABEL);
    }
    return rows;
  }, [hiddenSuppliers]);

  const toggleSupplier = (name: string) => {
    setHiddenSuppliers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const lowestPriceByDate = useMemo(() => deriveLowestPriceByDate(visibleDailyDetail), [visibleDailyDetail]);

  const getCellHeatmap = (supplier: string, date: string) => {
    const price = supplierPrices[supplier]?.[date] ?? null;
    const lowestPrice = lowestPriceByDate[date] ?? null;
    return getHeatmapColors(price, lowestPrice);
  };

  const handleDownloadCsv = useCallback(() => {
    const allSuppliers = [...TRACKED_OTAS, LE_SUPPLIER_LABEL, LE_VS_BEST_ROW_LABEL, BEST_COMPETITOR_LABEL];
    const dateLabels = visibleDates.map((d) => fmtShortDate(d));
    const headers = ['Supplier', ...dateLabels];
    const rows: Array<Record<string, unknown>> = allSuppliers.map((supplier) => {
      const row: Record<string, unknown> = { Supplier: supplier };
      visibleDates.forEach((date, i) => {
        const lePrice = supplierPrices[LE_SUPPLIER_LABEL]?.[date] ?? null;
        const bestPrice = supplierPrices[BEST_COMPETITOR_LABEL]?.[date] ?? null;
        if (supplier === LE_VS_BEST_ROW_LABEL) {
          const v = matrixCellExportValue(leVsBestDisplayMode, lePrice, bestPrice);
          row[dateLabels[i]] = v != null ? v : '';
          return;
        }
        const price = supplierPrices[supplier]?.[date] ?? null;
        const lowestPrice = lowestPriceByDate[date] ?? null;
        const v = matrixCellExportValue('absolute', price, lowestPrice);
        row[dateLabels[i]] = v != null ? v : '';
      });
      return row;
    });
    downloadCsvFile(rows, headers, `parity_matrix_${analysis.currency}_${visibleDates[0] ?? 'unknown'}.csv`);
  }, [visibleDates, supplierPrices, lowestPriceByDate, leVsBestDisplayMode, analysis.currency]);

  const tableRef = useRef<HTMLDivElement>(null);

  const handleShareScreenshot = useCallback(async () => {
    if (!tableRef.current) {
      return;
    }
    await downloadDomElementAsPng(
      tableRef.current,
      `parity_matrix_${analysis.currency}_${visibleDates[0] ?? 'unknown'}.png`,
    );
  }, [analysis.currency, visibleDates]);

  const stickyColumnSx = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    whiteSpace: 'nowrap',
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Competitive Calendar Matrix</Typography>
        <Box>
          <IconButton onClick={handleShareScreenshot} title="Download snapshot">
            <Download />
          </IconButton>
          <IconButton onClick={handleDownloadCsv} title="Download CSV">
            <TableChart />
          </IconButton>
        </Box>
      </Stack>

      <Box sx={{ display: 'flex', gap: 2, mb: PARITY_SECTION_BLOCK_MB, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="calendar-matrix-le-vs-best-label">LE vs best as</InputLabel>
          <Select<LeVsBestDisplayMode>
            labelId="calendar-matrix-le-vs-best-label"
            value={leVsBestDisplayMode}
            label="LE vs best as"
            onChange={(e) => setLeVsBestDisplayMode(e.target.value as LeVsBestDisplayMode)}
          >
            <MenuItem value="percentDiff">Percentage difference</MenuItem>
            <MenuItem value="priceDiff">Price difference</MenuItem>
          </Select>
        </FormControl>
        <SupplierFilterChips
          suppliers={TRACKED_OTAS}
          isActive={(ota) => !hiddenSuppliers.has(ota)}
          onToggle={toggleSupplier}
          label="Filter Suppliers:"
          showLeChip
          showBestCompetitorChip
          isBestCompetitorActive={!hiddenSuppliers.has(BEST_COMPETITOR_LABEL)}
          onToggleBestCompetitor={() => toggleSupplier(BEST_COMPETITOR_LABEL)}
        />
      </Box>

      <TableContainer ref={tableRef} component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyColumnSx,
                  zIndex: 3,
                  bgcolor: 'grey.100',
                }}
              />
              {monthGroups.map((group) => (
                <TableCell
                  key={group.month}
                  colSpan={group.count}
                  align="center"
                  sx={{
                    bgcolor: 'grey.100',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {group.month}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyColumnSx,
                  zIndex: 3,
                  bgcolor: 'grey.100',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              />
              {visibleDates.map((date) => (
                <TableCell
                  key={date}
                  align="center"
                  sx={{
                    bgcolor: 'grey.100',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmtShortDate(date)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((supplier) => {
              const isLeVsBestRow = supplier === LE_VS_BEST_ROW_LABEL;
              return (
                <TableRow
                  key={supplier}
                  sx={
                    isLeVsBestRow
                      ? {
                          '& > td': {
                            borderTopWidth: 3,
                            borderTopStyle: 'solid',
                            borderTopColor: 'divider',
                          },
                        }
                      : undefined
                  }
                >
                  <TableCell
                    sx={{
                      ...stickyColumnSx,
                      fontWeight: 600,
                      ...(isLeVsBestRow ? { bgcolor: 'grey.100' } : {}),
                    }}
                  >
                    {supplier}
                  </TableCell>
                  {visibleDates.map((date) => {
                    const price = supplierPrices[supplier]?.[date] ?? null;
                    const lowestPrice = lowestPriceByDate[date] ?? null;
                    const lePrice = supplierPrices[LE_SUPPLIER_LABEL]?.[date] ?? null;
                    const bestPrice = supplierPrices[BEST_COMPETITOR_LABEL]?.[date] ?? null;

                    let displayText: string;
                    let heatmap: ReturnType<typeof getHeatmapColors>;
                    if (isLeVsBestRow) {
                      displayText = formatMatrixCellDisplay(leVsBestDisplayMode, analysis.currency, lePrice, bestPrice);
                      heatmap = getHeatmapColors(lePrice, bestPrice);
                    } else {
                      displayText = formatMatrixCellDisplay('absolute', analysis.currency, price, lowestPrice);
                      heatmap = getCellHeatmap(supplier, date);
                    }

                    return (
                      <TableCell
                        key={date}
                        align="right"
                        sx={{
                          py: 2,
                          px: 1,
                          fontSize: 14,
                          fontWeight: 600,
                          border: 1,
                          borderColor: 'divider',
                          ...(isLeVsBestRow ? { bgcolor: 'grey.50' } : {}),
                        }}
                      >
                        {heatmap ? (
                          <Box
                            component="span"
                            sx={{
                              ...heatmap,
                              borderRadius: '6px',
                              px: 1,
                              py: 0.5,
                            }}
                          >
                            {displayText}
                          </Box>
                        ) : (
                          displayText
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalDays > 1 && (
        <DateRangeSlider
          dates={dates}
          range={[dateRange[0], dateRange[1]]}
          onRangeChange={setDateRange}
          onRangeCommit={handleRangeCommit}
        />
      )}
    </Box>
  );
}

export default CalendarRateView;
